import { dbAdapter } from './databaseAdapter';
import { customerService } from './customerService';
import { transactionService } from './transactionService';
import { settingService } from './settingService';


// HELPER FUNCTIONS
/**
 * Hitung progress otomatis berdasarkan status dan tanggal
 * @param {string} status - Status WO (pending, in-progress, completed, delivered, cancelled)
 * @param {string} startDate - Tanggal mulai (opsional)
 * @param {string} targetDate - Target selesai (opsional)
 * @returns {number} Progress 0-100
 */
const calculateProgress = (status, startDate, targetDate) => {
  // 🔥 KALO STATUS SUDAH FINAL
  if (status === 'completed' || status === 'delivered') return 100;
  if (status === 'cancelled') return 0;
  
  // 🔥 HITUNG BERDASARKAN TANGGAL
  if (startDate && targetDate) {
    const start = new Date(startDate);
    const target = new Date(targetDate);
    const now = new Date();
    
    // 🔥 KALO BELUM MULAI
    if (now < start) return 0;
    
    // 🔥 KALO SUDAH MELEWATI TARGET
    if (now > target) return 100;
    
    const total = target - start;
    const elapsed = now - start;
    
    if (total > 0) {
      const progress = Math.min(Math.round((elapsed / total) * 100), 100);
      return Math.max(progress, 0);
    }
  }
  
  // 🔥 FALLBACK BERDASARKAN STATUS
  const statusMap = {
    'pending': 0,
    'in-progress': 50,
    'completed': 100,
    'delivered': 100,
    'cancelled': 0
  };
  
  return statusMap[status] || 0;
};

/**
 * Validasi stok material sebelum create/update
 * @param {Array} materials - Array material dengan id dan qty
 * @returns {Promise<boolean>}
 */
const validateStock = async (materials) => {
  if (!materials || materials.length === 0) return true;
  
  // Ambil semua inventory
  const inventory = await dbAdapter.get('inventory');
  
  for (const material of materials) {
    const qty = parseInt(material.qty) || 1;
    if (qty <= 0) {
      throw new Error(`Qty untuk ${material.name || material.id} harus lebih dari 0`);
    }
    
    // Cari di inventory berdasarkan ID
    const invItem = inventory.find(item => item.id === material.id);
    
    if (!invItem) {
      throw new Error(`Material ${material.name || material.id} tidak ditemukan di inventory`);
    }
    
    // Cek stok
    if (invItem.stock < qty) {
      throw new Error(`Stok ${invItem.name} tidak cukup! Sisa: ${invItem.stock}, Dibutuhkan: ${qty}`);
    }
  }
  
  return true;
};

// STATUS FLOW CONFIGURATION
export const STATUS_FLOW = {
  'pending': {
    label: 'Order Masuk',
    icon: 'FiClock',
    color: 'bg-yellow-100 text-yellow-800',
    dotColor: 'bg-yellow-500',
    borderColor: 'border-yellow-500',
    next: ['in-progress', 'cancelled'],
    prev: []
  },
  'in-progress': {
    label: 'In Progress',
    icon: 'FiTrendingUp',
    color: 'bg-blue-100 text-blue-800',
    dotColor: 'bg-blue-500',
    borderColor: 'border-blue-500',
    next: ['completed', 'cancelled'],
    prev: ['pending']
  },
  'completed': {
    label: 'Selesai',
    icon: 'FiCheckCircle',
    color: 'bg-green-100 text-green-800',
    dotColor: 'bg-green-500',
    borderColor: 'border-green-500',
    next: ['delivered'],
    prev: ['in-progress']
  },
  'delivered': {
    label: 'Dikirim / Diterima',
    icon: 'FiTruck',
    color: 'bg-purple-100 text-purple-800',
    dotColor: 'bg-purple-500',
    borderColor: 'border-purple-500',
    next: [],
    prev: ['completed']
  },
  'cancelled': {
    label: 'Dibatalkan',
    icon: 'FiXCircle',
    color: 'bg-red-100 text-red-800',
    dotColor: 'bg-red-500',
    borderColor: 'border-red-500',
    next: [],
    prev: ['pending', 'in-progress']
  },
  // 🔥 TAMBAHKAN INI
  'progress': {
    label: 'Update Progress',
    icon: 'FiTrendingUp',
    color: 'bg-blue-100 text-blue-800',
    dotColor: 'bg-blue-500',
    borderColor: 'border-blue-500',
    next: [],
    prev: []
  }
};


// WORK ORDER SERVICE
export const workOrderService = {
  // ---------- CRUD ----------
  getAll: async () => {
    try {
      return await dbAdapter.get('work_orders');
    } catch (error) {
      console.error('Error getting all work orders:', error);
      throw new Error(error.message || 'Gagal mengambil data work order');
    }
  },

  getById: async (id) => {
    try {
      const result = await dbAdapter.getById('work_orders', id);
      if (!result) {
        console.warn(`Work order with id ${id} not found`);
        return null;
      }
      return result;
    } catch (error) {
      console.error('Error getting work order by id:', error);
      throw new Error(error.message || 'Gagal mengambil data work order');
    }
  },

  getByCategory: async (category) => {
    try {
      const orders = await dbAdapter.get('work_orders');
      return orders.filter(wo => wo.category === category);
    } catch (error) {
      console.error('Error getting work orders by category:', error);
      throw new Error(error.message || 'Gagal mengambil data work order');
    }
  },

  generateWONumber: async () => {
    try {
      const all = await dbAdapter.get('work_orders');
      const year = new Date().getFullYear();
      const yearWOs = all.filter(wo => wo.woNumber && wo.woNumber.startsWith(`WO-${year}`));
      const next = yearWOs.length + 1;
      return `WO-${year}-${String(next).padStart(3, '0')}`;
    } catch (error) {
      console.error('Error generating WO number:', error);
      throw new Error('Gagal generate nomor WO');
    }
  },

  //CREATE
  create: async (data) => {
    try {
      //VALIDASI STOK SEBELUM CREATE
      if (data.materials && data.materials.length > 0) {
        await validateStock(data.materials);
      }
      
      const woNumber = await workOrderService.generateWONumber();
      
      //HITUNG PROGRESS OTOMATIS
      const progress = calculateProgress(
        data.status || 'pending',
        data.startDate,
        data.targetDate
      );
      
      const newWO = {
        id: woNumber,
        woNumber: woNumber,
        ...data,
        status: data.status || 'pending',
        paymentStatus: 'pending',
        materials: Array.isArray(data.materials) ? data.materials : [],
        laborCost: parseInt(data.laborCost) || 0,
        totalCost: parseInt(data.totalCost) || 0,
        projectCode: data.projectCode || '',
        estimatedDays: parseInt(data.estimatedDays) || 0,
        blueprint: data.blueprint || null,
        //FIELD BARU UNTUK TEAM & PROGRESS
        team: Array.isArray(data.team) ? data.team : [],
        progress: data.progress !== undefined ? data.progress : progress,
        estimatedHours: parseInt(data.estimatedHours) || 0,
        actualHours: parseInt(data.actualHours) || 0,
        timeline: [
          { date: new Date().toISOString(), status: 'Order Masuk', note: 'WO dibuat', changedBy: 'System' }
        ],
        history: [{
          id: `hist-${Date.now()}`,
          fromStatus: null,
          toStatus: 'pending',
          note: 'WO dibuat',
          changedBy: 'System',
          timestamp: new Date().toISOString(),
          fromLabel: '-',
          toLabel: 'Order Masuk'
        }],
        files: Array.isArray(data.files) ? data.files : [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const result = await dbAdapter.create('work_orders', newWO);
      if (!result) {
        throw new Error('Gagal menyimpan ke database');
      }

      if (newWO.customerId) {
        await customerService.updateStats(newWO.customerId);
      }

      return result;
    } catch (error) {
      console.error('Error creating work order:', error);
      throw new Error(error.message || 'Gagal membuat work order');
    }
  },

  //UPDATE
  update: async (id, data) => {
    try {
      //VALIDASI STOK SEBELUM UPDATE
      const existingWO = await dbAdapter.getById('work_orders', id);
      
      if (data.materials && data.materials.length > 0) {
        const existingMaterials = existingWO?.materials || [];
        const materialsToCheck = data.materials.filter(newMat => {
          const existing = existingMaterials.find(oldMat => oldMat.id === newMat.id);
          if (!existing) return true;
          return parseInt(existing.qty) !== parseInt(newMat.qty);
        });
        
        if (materialsToCheck.length > 0) {
          await validateStock(materialsToCheck);
        }
      }
      
      //HITUNG PROGRESS OTOMATIS (kalau ada perubahan status/date)
      const progress = data.progress !== undefined 
        ? data.progress 
        : calculateProgress(
            data.status || existingWO?.status || 'pending',
            data.startDate || existingWO?.startDate,
            data.targetDate || existingWO?.targetDate
          );
      
      const updateData = {
        ...data,
        //FIELD BARU UNTUK TEAM & PROGRESS
        team: data.team !== undefined ? data.team : existingWO?.team || [],
        progress: progress,
        estimatedHours: data.estimatedHours !== undefined ? parseInt(data.estimatedHours) : existingWO?.estimatedHours || 0,
        actualHours: data.actualHours !== undefined ? parseInt(data.actualHours) : existingWO?.actualHours || 0,
        updatedAt: new Date().toISOString()
      };
      
      return await dbAdapter.update('work_orders', id, updateData);
    } catch (error) {
      console.error('Error updating work order:', error);
      throw new Error(error.message || 'Gagal mengupdate work order');
    }
  },

  //UPDATE STATUS (DENGAN DURASI & PROGRESS OTOMATIS)
  updateStatus: async (id, newStatus, note = '', userId = 'system') => {
    try {
      console.log('🔍 [updateStatus] START');
      console.log('📌 ID:', id);
      console.log('📌 New Status:', newStatus);

      const wo = await dbAdapter.getById('work_orders', id);
      if (!wo) {
        console.log('❌ WO tidak ditemukan');
        return null;
      }

      console.log('📦 WO ditemukan:', wo.id);
      console.log('📦 Status sekarang:', wo.status);
      console.log('📦 Materials:', wo.materials);

      // Validasi status
      if (!workOrderService.canTransitionTo(wo.status, newStatus)) {
        const flow = STATUS_FLOW[wo.status];
        const allowed = flow ? flow.next.join(', ') : 'tidak ada';
        throw new Error(`Status tidak valid. Hanya bisa ke: ${allowed}`);
      }

      // CEK KALO WO UDAH PAID DI KASIR
      if (newStatus === 'in-progress') {
        try {
          const transactions = await transactionService.getByWorkOrder(id);
          const hasPaid = transactions?.some(t => t.paymentStatus === 'paid');
          
          if (hasPaid) {
            console.warn('⚠️ [PAYMENT] WO ini sudah dibayar!');
            throw new Error('WO ini sudah dibayar, tidak bisa diubah ke In Progress. Batalkan pembayaran lebih dulu.');
          }
        } catch (error) {
          console.warn('⚠️ Gagal cek transaksi:', error.message);
        }
      }

      // STOCK DEDUCTION - in-progress ATAU completed!
      if ((newStatus === 'in-progress' || newStatus === 'completed') && wo.materials && wo.materials.length > 0) {
        console.log('🔽 [STOCK] Mengurangi stok untuk', wo.materials.length, 'material...');
        console.log('🔍 [STOCK] Materials:', JSON.stringify(wo.materials, null, 2));

        const inventory = await dbAdapter.get('inventory');
        console.log('📦 Inventory saat ini:', inventory.map(i => ({ id: i.id, name: i.name, stock: i.stock })));

        for (const material of wo.materials) {
          let invItem = inventory.find(item => item.id === material.id);
          
          if (!invItem && material.inventoryId) {
            console.warn(`⚠️ Material ${material.id} tidak ditemukan, coba cari dengan inventoryId: ${material.inventoryId}`);
            invItem = inventory.find(item => item.id === material.inventoryId);
            if (invItem) {
              console.warn(`✅ Found material with inventoryId: ${material.inventoryId}`);
              material.id = material.inventoryId;
            }
          }
          
          if (!invItem) {
            console.error(`❌ Material ${material.name || material.id} tidak ditemukan di inventory!`);
            console.error('📌 Available inventory IDs:', inventory.map(i => i.id));
            throw new Error(`Material ${material.name || material.id} tidak ditemukan di inventory`);
          }

          const qty = parseInt(material.qty) || 1;
          console.log(`📊 Stok ${invItem.name}: ${invItem.stock} → ${invItem.stock - qty}`);

          if (invItem.stock < qty) {
            throw new Error(`Stok ${invItem.name} tidak cukup! Sisa: ${invItem.stock}, Dibutuhkan: ${qty}`);
          }

          const updateResult = await dbAdapter.update('inventory', invItem.id, { stock: invItem.stock - qty });
          console.log(`✅ Hasil update inventory:`, updateResult);
        }
      } else {
        console.log('⏭️ [STOCK] Skip: kondisi tidak terpenuhi');
      }

      // KALO STATUS = 'cancelled', KEMBALIKAN STOK
      if (newStatus === 'cancelled' && wo.materials && wo.materials.length > 0) {
        console.log('🔄 [STOCK] Mengembalikan stok...');

        const inventory = await dbAdapter.get('inventory');

        for (const material of wo.materials) {
          let invItem = inventory.find(item => item.id === material.id);
          
          if (!invItem && material.inventoryId) {
            console.warn(`⚠️ Material ${material.id} tidak ditemukan, coba cari dengan inventoryId: ${material.inventoryId}`);
            invItem = inventory.find(item => item.id === material.inventoryId);
          }
          
          if (invItem) {
            const qty = parseInt(material.qty) || 1;
            console.log(`📊 Stok ${invItem.name}: ${invItem.stock} → ${invItem.stock + qty}`);
            await dbAdapter.update('inventory', invItem.id, { stock: invItem.stock + qty });
          } else {
            console.warn(`⚠️ Material ${material.name || material.id} tidak ditemukan di inventory, stok tidak dikembalikan`);
          }
        }
      }

      // HITUNG DURASI DARI STATUS SEBELUMNYA
      const lastTimeline = wo.timeline && wo.timeline.length > 0 
        ? wo.timeline[wo.timeline.length - 1] 
        : null;

      let durationMs = 0;
      let durationStr = null;

      if (lastTimeline && lastTimeline.date) {
        const prevDate = new Date(lastTimeline.date);
        const currentDate = new Date();
        durationMs = currentDate - prevDate;
        durationStr = workOrderService.formatDuration(durationMs);
      }

      // UPDATE STATUS WO
      const currentUser = JSON.parse(localStorage.getItem('eos7-user') || '{}');
      const userName = currentUser.name || 'System';

      const historyEntry = {
        id: `hist-${Date.now()}`,
        fromStatus: wo.status,
        toStatus: newStatus,
        note: note || '',
        changedBy: userName,
        userId: userId || 'system',
        timestamp: new Date().toISOString(),
        fromLabel: workOrderService.getStatusLabel(wo.status),
        toLabel: workOrderService.getStatusLabel(newStatus)
      };

      // TIMELINE ENTRY DENGAN DURASI
      const timelineEntry = {
        date: new Date().toISOString(),
        status: newStatus,
        note: note || '',
        changedBy: userName,
        duration: durationStr,
        durationMs: durationMs
      };

      const timeline = [...wo.timeline, timelineEntry];
      const history = [...(wo.history || []), historyEntry];

      //HITUNG PROGRESS OTOMATIS BERDASARKAN STATUS BARU
      const newProgress = calculateProgress(
        newStatus,
        wo.startDate,
        wo.targetDate
      );

      const updates = {
        status: newStatus,
        timeline,
        history,
        progress: newProgress,
        updatedAt: new Date().toISOString(),
        ...(newStatus === 'completed' ? {
          actualDate: new Date().toISOString(),
          paymentStatus: 'pending'
        } : {})
      };

      if (newStatus !== 'completed') {
        updates.paymentStatus = wo.paymentStatus || 'pending';
      }

      const result = await dbAdapter.update('work_orders', id, updates);
      console.log('✅ WO status updated:', result);


      //OTOMATIS BUAT TRANSAKSI KALO WO COMPLETED!
      if (newStatus === 'completed' && wo.customerId) {
        console.log('💰 [TRANSACTION] Membuat transaksi otomatis untuk WO:', wo.id);

        try {
          const existingTransactions = await transactionService.getByWorkOrder(wo.id);
          console.log('📊 [TRANSACTION] Existing transactions:', existingTransactions?.length || 0);

          if (!existingTransactions || existingTransactions.length === 0) {
            console.log('📝 [TRANSACTION] Membuat transaksi baru...');

            //1. BUAT ITEMS DARI MATERIAL
            const items = (wo.materials || []).map(m => ({
              inventoryId: m.id,
              name: m.name || 'Material',
              quantity: parseInt(m.qty) || 1,
              unit: 'pcs',
              price: parseInt(m.price) || 0,
              total: (parseInt(m.qty) || 1) * (parseInt(m.price) || 0)
            }));
            
            //2. TAMBAHKAN LABOR SEBAGAI ITEM
            if (wo.laborCost > 0) {
              items.push({
                inventoryId: null,
                name: 'Biaya Pekerjaan / Labor',
                quantity: 1,
                unit: 'jasa',
                price: parseInt(wo.laborCost) || 0,
                total: parseInt(wo.laborCost) || 0
              });
            }

            //3. HITUNG SUBTOTAL
            const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);

            //4. AMBIL TAX RATE DARI SETTING
            let taxRate = 11;
            try {
              const rate = await settingService.getTaxRate();
              taxRate = (rate && rate > 0) ? rate : 11;
            } catch (e) {
              console.warn('⚠️ Gagal ambil tax rate, pake default 11%');
            }
            const tax = Math.round(subtotal * (taxRate / 100));
            const total = subtotal + tax;

            //5. BUAT DATA TRANSAKSI
            const transactionData = {
              workOrderId: wo.id,
              customerId: wo.customerId,
              date: new Date().toISOString().split('T')[0],
              items: items,
              discount: 0,
              discountPercent: 0,
              markup: 0,
              markupPercent: 0,
              tax: tax,
              taxPercent: taxRate,
              taxName: 'PPN',
              paymentMethod: 'cash',
              paymentStatus: 'pending',
              notes: `Otomatis dari Work Order: ${wo.id}`,
              subtotal: subtotal,
              total: total
            };

            console.log('📤 [TRANSACTION] Data transaksi:', transactionData);

            const newTransaction = await transactionService.create(transactionData);
            console.log('✅ [TRANSACTION] Transaksi otomatis dibuat:', newTransaction?.id);
          } else {
            console.log('⏭️ [TRANSACTION] Transaksi sudah ada untuk WO ini');
          }
        } catch (error) {
          console.error('❌ [TRANSACTION] Gagal membuat transaksi otomatis:', error);
        }
      }

      // Update customer stats
      if (newStatus === 'completed' && wo.customerId) {
        await customerService.updateStats(wo.customerId);
      }

      return result;
    } catch (error) {
      console.error('❌ Error updating status:', error);
      throw new Error(error.message || 'Gagal mengupdate status');
    }
  },

  //HELPER: DURATION
  formatDuration: (ms) => {
    if (!ms || ms < 0) return null;
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    
    if (minutes > 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 
        ? `${hours}j ${remainingMinutes}m` 
        : `${hours}j`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else if (seconds > 0) {
      return `${seconds}s`;
    }
    return null;
  },

  //HELPER: TIMELINE WITH DURATION
  getTimelineWithDuration: (wo) => {
    if (!wo || !wo.timeline) return [];
    
    return wo.timeline.map((entry, index) => {
      if (index === 0) {
        return { ...entry, duration: null, durationMs: 0 };
      }
      
      const prevEntry = wo.timeline[index - 1];
      if (!prevEntry || !prevEntry.date) {
        return { ...entry, duration: null, durationMs: 0 };
      }
      
      const prevDate = new Date(prevEntry.date);
      const currentDate = new Date(entry.date);
      const durationMs = currentDate - prevDate;
      
      return {
        ...entry,
        duration: workOrderService.formatDuration(durationMs),
        durationMs: durationMs
      };
    });
  },

  //HELPER: STATUS
  getHistory: async (id) => {
    try {
      const wo = await dbAdapter.getById('work_orders', id);
      return wo ? wo.history || [] : [];
    } catch (error) {
      console.error('Error getting history:', error);
      return [];
    }
  },

  getLatestStatusChange: async (id) => {
    const history = await workOrderService.getHistory(id);
    return history.length > 0 ? history[history.length - 1] : null;
  },

  getStatusChangeCount: async (id) => {
    const history = await workOrderService.getHistory(id);
    return history.length;
  },

  getNextStatuses: (currentStatus) => {
    const flow = STATUS_FLOW[currentStatus];
    return flow ? flow.next : [];
  },

  canTransitionTo: (currentStatus, newStatus) => {
    const flow = STATUS_FLOW[currentStatus];
    if (!flow) return false;
    return flow.next.includes(newStatus);
  },

  getStatusLabel: (status) => {
    const flow = STATUS_FLOW[status];
    return flow ? flow.label : status;
  },

  getStatusColor: (status) => {
    const flow = STATUS_FLOW[status];
    return flow ? flow.color : 'bg-gray-100 text-gray-800';
  },

  getStatusIcon: (status) => {
    const flow = STATUS_FLOW[status];
    return flow ? flow.icon : 'FiClock';
  },

  getDotColor: (status) => {
    const flow = STATUS_FLOW[status];
    return flow ? flow.dotColor : 'bg-gray-500';
  },

  //DELETE
  delete: async (id) => {
    try {
      return await dbAdapter.delete('work_orders', id);
    } catch (error) {
      console.error('Error deleting work order:', error);
      throw new Error(error.message || 'Gagal menghapus work order');
    }
  },

  //DASHBOARD STATS
  getDashboardStats: async () => {
  try {
    const all = await dbAdapter.get('work_orders');
    const customers = await dbAdapter.get('customers');
    const staff = await dbAdapter.get('staff');

    return {
      totalOrders: all.length,
      pending: all.filter(wo => wo.status === 'pending').length,
      inProgress: all.filter(wo => wo.status === 'in-progress').length,
      completed: all.filter(wo => wo.status === 'completed').length,
      delivered: all.filter(wo => wo.status === 'delivered').length,  
      totalCustomers: customers.length,
      totalStaff: staff.length,
      completionRate: all.length > 0
        ? Math.round((all.filter(wo => wo.status === 'completed' || wo.status === 'delivered').length / all.length) * 100)
        : 0
    };
  } catch (error) {
    console.error('Error getting dashboard stats:', error);
    return {
      totalOrders: 0,
      pending: 0,
      inProgress: 0,
      completed: 0,
      delivered: 0,
      totalCustomers: 0,
      totalStaff: 0,
      completionRate: 0
    };
  }
},

  //CREATE WITH TRANSACTION
  createWithTransaction: async (woData, transactionData) => {
    try {
      const newWO = await workOrderService.create(woData);

      if (transactionData && transactionData.items && transactionData.items.length > 0) {
        const transaction = {
          workOrderId: newWO.id,
          customerId: newWO.customerId,
          date: new Date().toISOString(),
          items: transactionData.items,
          subtotal: 0,
          tax: 0,
          discount: transactionData.discount || 0,
          total: 0,
          paymentMethod: transactionData.paymentMethod || 'cash',
          paymentStatus: transactionData.paymentStatus || 'pending',
          notes: transactionData.notes || '',
          paymentDate: transactionData.paymentStatus === 'paid' ? new Date().toISOString() : null
        };

        const newTransaction = await transactionService.create(transaction);
        return { workOrder: newWO, transaction: newTransaction };
      }

      return { workOrder: newWO };
    } catch (error) {
      console.error('Error creating work order with transaction:', error);
      throw new Error(error.message || 'Gagal membuat work order dengan transaksi');
    }
  },

  //TRANSACTION
  getTransaction: async (id) => {
    try {
      return await transactionService.getByWorkOrder(id);
    } catch (error) {
      console.error('Error getting transaction:', error);
      return null;
    }
  },

  //FILES
  getFiles: async (id) => {
    try {
      const wo = await dbAdapter.getById('work_orders', id);
      return wo ? wo.files || [] : [];
    } catch (error) {
      console.error('Error getting files:', error);
      return [];
    }
  },

  getImages: async (id) => {
    const files = await workOrderService.getFiles(id);
    return files.filter(f => f.type && f.type.startsWith('image/'));
  },

  getPDFs: async (id) => {
    const files = await workOrderService.getFiles(id);
    return files.filter(f => f.type === 'application/pdf');
  },

 
  //TEAM & PROGRESS METHODS (BARU)
  /**
   * Tambah member ke team
   * @param {string} woId - ID Work Order
   * @param {object} member - { id, name, role, hours, rate, isLead }
   * @returns {Promise<object>} Updated WO
   */
  addTeamMember: async (woId, member) => {
    try {
      const wo = await workOrderService.getById(woId);
      if (!wo) throw new Error('WO tidak ditemukan');
      
      const team = [...(wo.team || [])];
      // Cek apa sudah ada
      const exists = team.find(m => m.id === member.id);
      if (exists) throw new Error('Staff sudah ada di tim');
      
      team.push({
        id: member.id,
        name: member.name || member.id,
        role: member.role || 'Anggota',
        photo: member.photo || '',
        hours: parseInt(member.hours) || 0,
        rate: parseInt(member.rate) || 0,
        isLead: member.isLead || false
      });
      
      // Update total estimatedHours
      const estimatedHours = team.reduce((sum, m) => sum + (m.hours || 0), 0);
      
      const result = await workOrderService.update(woId, { 
        team, 
        estimatedHours 
      });
      return result;
    } catch (error) {
      console.error('Error adding team member:', error);
      throw new Error(error.message || 'Gagal menambah member tim');
    }
  },

  /**
   * Hapus member dari team
   * @param {string} woId - ID Work Order
   * @param {string} staffId - ID Staff yang dihapus
   * @returns {Promise<object>} Updated WO
   */
  removeTeamMember: async (woId, staffId) => {
    try {
      const wo = await workOrderService.getById(woId);
      if (!wo) throw new Error('WO tidak ditemukan');
      
      let team = wo.team || [];
      team = team.filter(m => m.id !== staffId);
      
      // Update total estimatedHours
      const estimatedHours = team.reduce((sum, m) => sum + (m.hours || 0), 0);
      
      const result = await workOrderService.update(woId, { 
        team, 
        estimatedHours 
      });
      return result;
    } catch (error) {
      console.error('Error removing team member:', error);
      throw new Error(error.message || 'Gagal menghapus member tim');
    }
  },

  /**
 * Update progress WO
 * @param {string} woId - ID Work Order
 * @param {number} progress - Nilai progress 0-100
 * @param {string} note - Catatan (opsional)
 * @returns {Promise<object>} Updated WO
 */
updateProgress: async (woId, progress, note = '') => {
  try {
    if (progress < 0 || progress > 100) {
      throw new Error('Progress harus antara 0-100');
    }
    
    const wo = await workOrderService.getById(woId);
    if (!wo) throw new Error('WO tidak ditemukan');
    
    // 🔥 VALIDASI: TIDAK BISA UPDATE PROGRESS KALO STATUS FINAL
    if (wo.status === 'cancelled') {
      throw new Error('WO sudah dibatalkan, tidak bisa update progress');
    }
    
    if (wo.status === 'completed' || wo.status === 'delivered') {
      throw new Error('WO sudah selesai, tidak bisa update progress manual');
    }
    
    // Tambahkan ke timeline dengan status 'progress'
    const timeline = [...(wo.timeline || []), {
      date: new Date().toISOString(),
      status: 'progress',
      note: `Progress: ${progress}% ${note ? '- ' + note : ''}`,
      changedBy: 'System',
      progress: progress
    }];
    
    const result = await workOrderService.update(woId, { 
      progress, 
      timeline 
    });
    return result;
  } catch (error) {
    console.error('Error updating progress:', error);
    throw new Error(error.message || 'Gagal mengupdate progress');
  }
},

  /**
   * Get team members for WO
   * @param {string} woId - ID Work Order
   * @returns {Promise<Array>} Array of team members
   */
  getTeam: async (woId) => {
    try {
      const wo = await workOrderService.getById(woId);
      return wo ? wo.team || [] : [];
    } catch (error) {
      console.error('Error getting team:', error);
      return [];
    }
  },

  /**
   * Get progress for WO
   * @param {string} woId - ID Work Order
   * @returns {Promise<number>} Progress 0-100
   */
  getProgress: async (woId) => {
    try {
      const wo = await workOrderService.getById(woId);
      return wo ? wo.progress || 0 : 0;
    } catch (error) {
      console.error('Error getting progress:', error);
      return 0;
    }
  },

  /**
   * Update actual hours (realisasi jam kerja)
   * @param {string} woId - ID Work Order
   * @param {number} hours - Jam kerja aktual
   * @returns {Promise<object>} Updated WO
   */
  updateActualHours: async (woId, hours) => {
    try {
      if (hours < 0) throw new Error('Jam kerja tidak boleh negatif');
      
      const wo = await workOrderService.getById(woId);
      if (!wo) throw new Error('WO tidak ditemukan');
      
      const actualHours = parseInt(hours) || 0;
      
      const result = await workOrderService.update(woId, { actualHours });
      return result;
    } catch (error) {
      console.error('Error updating actual hours:', error);
      throw new Error(error.message || 'Gagal update jam kerja');
    }
  },

  /**
   * Get team member by staff ID
   * @param {string} woId - ID Work Order
   * @param {string} staffId - ID Staff
   * @returns {Promise<object|null>} Team member or null
   */
  getTeamMember: async (woId, staffId) => {
    try {
      const team = await workOrderService.getTeam(woId);
      return team.find(m => m.id === staffId) || null;
    } catch (error) {
      console.error('Error getting team member:', error);
      return null;
    }
  },

  /**
   * Update team member role/hours
   * @param {string} woId - ID Work Order
   * @param {string} staffId - ID Staff
   * @param {object} updates - { role, hours, rate, isLead }
   * @returns {Promise<object>} Updated WO
   */
  updateTeamMember: async (woId, staffId, updates) => {
    try {
      const wo = await workOrderService.getById(woId);
      if (!wo) throw new Error('WO tidak ditemukan');
      
      let team = wo.team || [];
      const index = team.findIndex(m => m.id === staffId);
      if (index === -1) throw new Error('Staff tidak ditemukan di tim');
      
      team[index] = {
        ...team[index],
        role: updates.role || team[index].role,
        hours: updates.hours !== undefined ? parseInt(updates.hours) : team[index].hours,
        rate: updates.rate !== undefined ? parseInt(updates.rate) : team[index].rate,
        isLead: updates.isLead !== undefined ? updates.isLead : team[index].isLead
      };
      
      // Update total estimatedHours
      const estimatedHours = team.reduce((sum, m) => sum + (m.hours || 0), 0);
      
      const result = await workOrderService.update(woId, { 
        team, 
        estimatedHours 
      });
      return result;
    } catch (error) {
      console.error('Error updating team member:', error);
      throw new Error(error.message || 'Gagal update member tim');
    }
  }
};